import { redirectUrlError, redirectDelayError } from '../utils/redirectUrl'
import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import {
  MessageSquare, History, QrCode, Settings, Home, Plus,
  Edit3, Link2, Copy, Check, Download,
  LogOut, X, Loader2, AlertCircle, UploadCloud, Printer, Trash2, Eye, Share2
} from 'lucide-react'
import QrMaker, { drawYowoOverlay } from './QrMaker'
import { buildApiUrl, buildQrUrl } from '../config'
import { isAccountLoggedIn, getAccessToken, accountSignOut } from '../accountAuth'
import { deleteMyCode } from '../api/accountApi'
import { Button, Card, Alert, Input } from './ui'
import { timeAgo } from '../utils/timeAgo'
import { CodeAppearance, defaultAppearance } from '../appearance'
import { shareCode } from '../native'

interface Message {
  handle: string
  message: string
  redirectDelaySeconds?: number
  redirectUrl?: string
  imageUrl?: string
  updatedAt: string
  viewCount?: number
  appearance?: CodeAppearance
}

type Tab = 'message' | 'history' | 'analytics' | 'qr' | 'settings'

interface Analytics {
  totalScans: number
  uniqueScans: number
  filteredBots: number
  daily: Array<{ date: string; scans: number }>
  weekly: Array<{ date: string; scans: number }>
  referrers: Record<string, number>
  regions: Record<string, number>
  retentionDays: number
}

const Dashboard = () => {
  const { handle } = useParams<{ handle: string }>()
  const navigate = useNavigate()
  const [message, setMessage] = useState<Message | null>(null)
  const [redirectUrl, setRedirectUrl] = useState('')
  const [redirectDelaySeconds, setRedirectDelaySeconds] = useState(0)
  const [newMessage, setNewMessage] = useState('')
  const [newImageUrl, setNewImageUrl] = useState('')
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)
  const [messageHistory, setMessageHistory] = useState<any[]>([])
  const [loadingHistory, setLoadingHistory] = useState(false)
  const [copied, setCopied] = useState(false)
  const [uploadError, setUploadError] = useState<string | null>(null)
  const [activeTab, setActiveTab] = useState<Tab>('message')
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [deleteError, setDeleteError] = useState<string | null>(null)
  const [appearance, setAppearance] = useState<CodeAppearance>(defaultAppearance)
  const [analytics, setAnalytics] = useState<Analytics | null>(null)

  useEffect(() => {
    if (!handle) return
    if (!isAccountLoggedIn()) {
      navigate(`/account/login?next=/dashboard/${handle}`)
      return
    }
    fetchMessage()
    fetchMessageHistory()
  }, [handle])

  const fetchMessage = async () => {
    try {
      setLoading(true)
      const response = await fetch(buildApiUrl(`/messages/${handle}`))
      if (response.status === 404) { setError('Code not found.'); return }
      if (!response.ok) throw new Error('Failed to fetch message')
      const data = await response.json()
      setMessage(data)
      setNewMessage(data.message || '')
      setNewImageUrl(data.imageUrl || '')
      setRedirectUrl(data.redirectUrl || '')
      setRedirectDelaySeconds(data.redirectDelaySeconds ?? 0)
      setAppearance(data.appearance || defaultAppearance)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred')
    } finally {
      setLoading(false)
    }
  }

  const handleUpdateMessage = async (e: React.FormEvent) => {
    e.preventDefault()
    const redirectError = redirectUrlError(redirectUrl) || (redirectUrl.trim() ? redirectDelayError(redirectDelaySeconds) : undefined)
    if (redirectError) { setError(redirectError); return }
    if (!newImageUrl) setNewImageUrl('')
    setSaving(true)
    setError(null)
    try {
      const token = await getAccessToken()
      const headers: Record<string, string> = { 'Content-Type': 'application/json' }
      if (token) headers['Authorization'] = `Bearer ${token}`
      const response = await fetch(buildApiUrl(`/messages/${handle}`), {
        method: 'PUT',
        headers,
        body: JSON.stringify({ message: newMessage.trim(), imageUrl: newImageUrl || undefined, redirectUrl: redirectUrl.trim(), redirectDelaySeconds: redirectUrl.trim() ? redirectDelaySeconds : 0, appearance })
      })
      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error || 'Failed to update message')
      }
      if (message) {
        setMessage({ ...message, message: newMessage.trim(), imageUrl: newImageUrl || undefined, redirectUrl: redirectUrl.trim(), redirectDelaySeconds: redirectUrl.trim() ? redirectDelaySeconds : 0, appearance, updatedAt: new Date().toISOString() })
      }
      await fetchMessageHistory()
      showSuccess('Message updated successfully!')
    } catch (error) {
      setError(error instanceof Error ? error.message : 'An error occurred')
    } finally {
      setSaving(false)
    }
  }

  const showSuccess = (msg: string) => {
    setSuccessMessage(msg)
    setTimeout(() => setSuccessMessage(null), 5000)
  }

  const fetchMessageHistory = async () => {
    if (!handle) return
    try {
      setLoadingHistory(true)
      const token = await getAccessToken()
      const response = await fetch(buildApiUrl(`/messages/${handle}/history`), {
        headers: token ? { Authorization: `Bearer ${token}` } : undefined
      })
      if (response.ok) {
        const data = await response.json()
        setMessageHistory(data.history || [])
      }
    } catch {
      // ignore
    } finally {
      setLoadingHistory(false)
    }
  }

  const fetchAnalytics = async () => {
    try {
      const token = await getAccessToken()
      if (!token) return
      const response = await fetch(buildApiUrl(`/messages/${handle}/analytics`), { headers: { Authorization: `Bearer ${token}` } })
      if (response.ok) setAnalytics(await response.json())
    } catch {
      setError('Could not load analytics.')
    }
  }

  const clearAnalytics = async () => {
    if (!window.confirm('Delete retained scan details? Aggregate counters will remain.')) return
    const token = await getAccessToken()
    const response = await fetch(buildApiUrl(`/messages/${handle}/analytics`), {
      method: 'DELETE',
      headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    })
    if (!response.ok) return setError('Could not clear analytics data')
    await fetchAnalytics()
    showSuccess('Retained scan details deleted')
  }

  useEffect(() => {
    if (activeTab === 'analytics') fetchAnalytics()
  }, [activeTab, handle])

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      setError('Could not copy the link. Select and copy the URL manually.')
    }
  }

  const handleDownloadSvg = async () => {
    if (!message) return
    try {
      const QRCode = (await import('qrcode')).default
      const url = buildQrUrl(message.handle)
      const svgString = await QRCode.toString(url, { type: 'svg', margin: 2 })
      const blob = new Blob([svgString], { type: 'image/svg+xml' })
      const downloadUrl = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = downloadUrl
      link.download = `qr-code-${message.handle}.svg`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(downloadUrl)
    } catch {
      setError('Could not download the SVG. Please try again.')
    }
  }

  const generateQrCanvas = async (size: number): Promise<HTMLCanvasElement | null> => {
    if (!message) return null
    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')
    if (!ctx) return null
    canvas.width = size
    canvas.height = size
    const QRCode = (await import('qrcode')).default
    const url = buildQrUrl(message.handle)
    await QRCode.toCanvas(canvas, url, { width: size, margin: 2, errorCorrectionLevel: 'H', color: { dark: appearance.qrDarkColor, light: appearance.qrLightColor } })
    if (appearance.qrFrame === 'label') drawYowoOverlay(ctx, size)
    return canvas
  }

  const handleDownloadQr = async () => {
    if (!message) return
    try {
      const canvas = await generateQrCanvas(1800)
      if (!canvas) return
      canvas.toBlob((blob) => {
        if (!blob) return
        const downloadUrl = URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = downloadUrl
        link.download = `qr-code-${message.handle}.png`
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        URL.revokeObjectURL(downloadUrl)
      }, 'image/png')
    } catch {
      window.open(buildQrUrl(message.handle), '_blank')
    }
  }

  const handleDownloadPrintSheet = async () => {
    try {
      const qr = await generateQrCanvas(420)
      if (!qr || !message) return
      const sheet = document.createElement('canvas')
      sheet.width = 2550
      sheet.height = 3300
      const ctx = sheet.getContext('2d')
      if (!ctx) return
      ctx.fillStyle = '#ffffff'
      ctx.fillRect(0, 0, sheet.width, sheet.height)
      ctx.fillStyle = '#0f172a'
      ctx.font = 'bold 48px Arial'
      ctx.textAlign = 'center'
      const columns = 3
      const rows = 4
      for (let row = 0; row < rows; row++) {
        for (let column = 0; column < columns; column++) {
          const x = 255 + column * 765
          const y = 220 + row * 760
          ctx.drawImage(qr, x, y, 510, 510)
          ctx.fillText(`youwont.xyz/u/${message.handle}`, x + 255, y + 575, 680)
        }
      }
      const link = document.createElement('a')
      link.href = sheet.toDataURL('image/png')
      link.download = `${message.handle}-letter-print-sheet.png`
      link.click()
    } catch {
      setError('Could not create the print sheet. Please try again.')
    }
  }

  const handleGetStickers = async () => {
    // Download the QR PNG, then open Sticker Mule so the user can upload it
    const canvas = await generateQrCanvas(1800)
    if (canvas) {
      canvas.toBlob((blob) => {
        if (!blob) return
        const downloadUrl = URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = downloadUrl
        link.download = `yowo-sticker-${message?.handle}.png`
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        URL.revokeObjectURL(downloadUrl)
      }, 'image/png')
    }
    window.open('https://www.stickermule.com/custom-stickers', '_blank', 'noopener,noreferrer')
  }

  const handleLogout = () => {
    accountSignOut()
    navigate('/')
  }

  const handleDeleteCode = async () => {
    if (!handle) return
    setDeleting(true)
    setDeleteError(null)
    try {
      await deleteMyCode(handle)
      navigate('/my-codes')
    } catch (err) {
      setDeleteError(err instanceof Error ? err.message : 'Failed to delete code')
      setDeleting(false)
      setConfirmDelete(false)
    }
  }

  // ── Loading ────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center p-4">
        <div className="text-center animate-fade-in">
          <Loader2 size={40} className="animate-spin text-brand mx-auto mb-4" />
          <p className="text-slate-500 dark:text-slate-400">Loading dashboard...</p>
        </div>
      </div>
    )
  }

  // ── Error ──────────────────────────────────────────────────────────────────
  if (error && !message) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center p-4">
        <div className="text-center animate-fade-in">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-accent-danger/10 mb-4">
            <AlertCircle size={28} className="text-accent-danger" />
          </div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100 mb-2">Something went wrong</h1>
          <p className="text-slate-500 dark:text-slate-400 mb-6">{error}</p>
          <Button variant="primary" onClick={() => navigate('/')}>
            <Home size={16} className="mr-2" />
            Go Home
          </Button>
        </div>
      </div>
    )
  }

  if (!message) return null

  const tabs: { id: Tab; label: string; icon: React.ReactNode }[] = [
    { id: 'message', label: 'Message', icon: <MessageSquare size={16} /> },
    { id: 'history', label: 'History', icon: <History size={16} /> },
    { id: 'analytics', label: 'Analytics', icon: <Eye size={16} /> },
    { id: 'qr', label: 'QR Code', icon: <QrCode size={16} /> },
    { id: 'settings', label: 'Settings', icon: <Settings size={16} /> },
  ]

  return (
    <>
      <Helmet>
        <title>Dashboard - {message.handle} - YouWont</title>
        <meta name="description" content="Manage your QR code message" />
      </Helmet>

      <div className="py-6 sm:py-8 lg:py-10 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto">

          {/* Header */}
          <div className="flex items-center justify-between mb-6 animate-fade-in">
            <div>
              <button
                type="button"
                onClick={() => navigate('/my-codes')}
                className="flex items-center gap-1 text-sm text-brand hover:text-brand-600 dark:text-blue-400 mb-2"
              >
                <Home size={13} />
                ← My Codes
              </button>
              <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 dark:text-slate-100">Dashboard</h1>
              <p className="text-slate-500 dark:text-slate-400 text-sm mt-0.5">
                Managing <span className="font-semibold text-slate-700 dark:text-slate-200">youwont.xyz/u/{message.handle}</span>
                {message.viewCount != null && message.viewCount > 0 && (
                  <span className="ml-2 text-slate-400 dark:text-slate-500">
                    · Scanned {message.viewCount.toLocaleString()} {message.viewCount === 1 ? 'time' : 'times'}
                  </span>
                )}
              </p>
            </div>
            <Button variant="ghost" size="sm" onClick={handleLogout} className="text-slate-500">
              <LogOut size={15} className="mr-1.5" />
              <span className="hidden sm:inline">Log Out</span>
            </Button>
          </div>

          {/* Alerts */}
          {successMessage && (
            <Alert variant="success" className="mb-4 animate-fade-in">
              <div className="flex items-center gap-2">
                <Check size={15} className="shrink-0" />
                {successMessage}
              </div>
            </Alert>
          )}
          {error && (
            <Alert variant="danger" className="mb-4 animate-fade-in">
              <div className="flex items-center gap-2">
                <AlertCircle size={15} className="shrink-0" />
                {error}
              </div>
            </Alert>
          )}

          {/* Tab Bar */}
          <div className="flex gap-1 p-1 bg-slate-100 dark:bg-slate-800 rounded-xl mb-6 animate-slide-up">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex-1 flex items-center justify-center gap-1.5 py-2 px-2 sm:px-3 rounded-lg text-xs sm:text-sm font-medium transition-all duration-200 ${
                  activeTab === tab.id
                    ? 'bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 shadow-soft'
                    : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200'
                }`}
              >
                {tab.icon}
                <span>{tab.label}</span>
              </button>
            ))}
          </div>

          {/* ── Message Tab ── */}
          {activeTab === 'message' && (
            <div className="space-y-5 animate-fade-in">
              <Card title={message.redirectUrl ? "Current Redirect" : "Current Message"}>
                {/* URL bar */}
                <div className="flex items-center gap-2 mb-4 px-3 py-2 rounded-xl bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700">
                  <span className="flex-1 text-sm font-mono text-slate-600 dark:text-slate-300 break-all sm:break-normal sm:truncate">
                    youwont.xyz/u/{message.handle}
                  </span>
                  <button
                    onClick={() => copyToClipboard(buildQrUrl(message.handle))}
                    className="shrink-0 flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-lg bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 text-slate-600 dark:text-slate-300 hover:border-brand hover:text-brand dark:hover:text-blue-400 transition-colors"
                  >
                    {copied ? <><Check size={12} className="text-accent-success" />Copied</> : <><Copy size={12} />Copy</>}
                  </button>
                </div>
                <div className="bg-slate-50 dark:bg-slate-800/50 rounded-xl p-5 space-y-4">
                  <p className="text-lg font-medium text-slate-900 dark:text-slate-100 whitespace-pre-wrap break-words text-center">
                    {message.redirectUrl ? `Scans open ${message.redirectDelaySeconds ? `after ${message.redirectDelaySeconds} seconds` : 'immediately'}: ${message.redirectUrl}` : message.message}
                  </p>
                  {message.imageUrl && !message.redirectUrl && (
                    <div className="flex justify-center">
                      <img
                        src={message.imageUrl}
                        alt="Current message image"
                        className="max-w-full h-auto rounded-xl shadow-soft max-h-48 object-contain"
                        onError={(e) => { e.currentTarget.style.display = 'none' }}
                      />
                    </div>
                  )}
                  <p className="text-xs text-slate-400 dark:text-slate-500 text-center">
                    Updated {timeAgo(message.updatedAt)}
                  </p>
                </div>
              </Card>

              <Card title="Update Message" action={
                <span className="text-xs text-slate-400 dark:text-slate-500">{newMessage.length}/500</span>
              }>
                <form onSubmit={handleUpdateMessage} className="space-y-4">
                  <textarea
                    rows={5}
                    className="w-full px-4 py-3 border border-slate-200 dark:border-slate-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand/40 focus:border-transparent bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 transition-all duration-200 text-base resize-none placeholder:text-slate-400 dark:placeholder:text-slate-500"
                    placeholder="Enter your new message..."
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    maxLength={500}
                  />

                  <Input
                    label="Redirect URL (optional)"
                    type="url"
                    placeholder="https://example.com"
                    value={redirectUrl}
                    onChange={(event) => setRedirectUrl(event.target.value)}
                    error={redirectUrlError(redirectUrl)}
                    maxLength={2048}
                    hint="Scans open this website. Add a delay below to show your message first, or clear this link to turn redirects off."
                  />

                  {redirectUrl.trim() && (
                    <Input
                      label="Redirect delay (seconds)"
                      type="number"
                      min={0}
                      max={3600}
                      step={1}
                      required
                      value={Number.isNaN(redirectDelaySeconds) ? '' : redirectDelaySeconds}
                      onChange={(event) => setRedirectDelaySeconds(event.target.valueAsNumber)}
                      error={redirectDelayError(redirectDelaySeconds)}
                      hint="0 opens the website immediately. Enter 1–3600 seconds to show your message and a countdown first."
                    />
                  )}

                  {/* Image upload */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                      Image (Optional)
                    </label>
                    <label className={`flex flex-col items-center justify-center w-full h-24 border-2 border-dashed rounded-xl cursor-pointer transition-colors ${uploading ? 'opacity-50 cursor-not-allowed' : 'border-slate-300 dark:border-slate-600 hover:border-brand dark:hover:border-brand hover:bg-brand/5'}`}>
                      <div className="flex flex-col items-center gap-1 pointer-events-none">
                        {uploading
                          ? <Loader2 size={20} className="animate-spin text-brand" />
                          : <UploadCloud size={20} className="text-slate-400 dark:text-slate-500" />
                        }
                        <span className="text-xs text-slate-500 dark:text-slate-400">
                          {uploading ? 'Uploading...' : 'Click to upload image'}
                        </span>
                      </div>
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        disabled={uploading}
                        onChange={async (e) => {
                          const file = e.target.files?.[0]
                          if (!file) return
                          try {
                            setUploading(true)
                            setUploadError(null)
                            const maxSize = 5 * 1024 * 1024
                            if (file.size > maxSize) throw new Error('File size exceeds 5MB limit')
                            if (!['image/jpeg', 'image/png', 'image/webp', 'image/gif'].includes(file.type)) {
                              throw new Error('Use a JPEG, PNG, WebP, or GIF image')
                            }
                            const token = await getAccessToken()
                            if (!token) throw new Error('Your session expired. Please log in again.')
                            const response = await fetch(buildApiUrl(`/upload-url/${message.handle}`), {
                              method: 'POST',
                              headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                              body: JSON.stringify({ contentType: file.type, fileSize: file.size })
                            })
                            if (!response.ok) throw new Error('Failed to get upload URL')
                            const { uploadUrl, imageUrl } = await response.json()
                            const uploadResponse = await fetch(uploadUrl, { method: 'PUT', body: file, headers: { 'Content-Type': file.type } })
                            if (!uploadResponse.ok) throw new Error('Upload failed')
                            setNewImageUrl(imageUrl)
                            e.target.value = ''
                            showSuccess('Image uploaded!')
                          } catch (error) {
                            setUploadError(error instanceof Error ? error.message : 'Upload failed')
                          } finally {
                            setUploading(false)
                          }
                        }}
                      />
                    </label>
                    {uploadError && (
                      <Alert variant="danger" className="mt-2">
                        <div className="flex items-center justify-between gap-2">
                          <span>{uploadError}</span>
                          <button onClick={() => setUploadError(null)} className="shrink-0 hover:opacity-70">
                            <X size={14} />
                          </button>
                        </div>
                      </Alert>
                    )}
                    {newImageUrl && (
                      <div className="mt-3 flex items-center gap-3">
                        <img src={newImageUrl} alt="Preview" className="w-16 h-16 object-cover rounded-lg border border-slate-200 dark:border-slate-700" />
                        <div>
                          <p className="text-xs font-medium text-slate-700 dark:text-slate-300">New image ready</p>
                          <button onClick={() => setNewImageUrl('')} className="text-xs text-accent-danger hover:underline mt-0.5">Remove</button>
                        </div>
                      </div>
                    )}
                    <p className="text-xs text-slate-400 dark:text-slate-500 mt-2">Leave empty to remove the current image</p>
                  </div>

                  <Button
                    type="submit"
                    variant="primary"
                    size="lg"
                    className="w-full"
                    disabled={saving || (newMessage === message.message && newImageUrl === (message.imageUrl || '') && redirectUrl.trim() === (message.redirectUrl || '') && redirectDelaySeconds === (message.redirectDelaySeconds ?? 0) && JSON.stringify(appearance) === JSON.stringify(message.appearance || defaultAppearance))}
                  >
                    {saving
                      ? <><Loader2 size={15} className="animate-spin mr-2" />Saving...</>
                      : <><Edit3 size={15} className="mr-2" />Update Message</>
                    }
                  </Button>
                </form>

                <div className="mt-5 p-4 bg-gradient-to-r from-brand/5 to-purple-50 dark:from-brand/10 dark:to-purple-900/20 rounded-xl border border-brand/10 dark:border-brand/20">
                  <p className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-1">Pro Move</p>
                  <p className="text-xs text-slate-500 dark:text-slate-400 leading-relaxed">
                    Print this on hats, shirts, or business cards. People will be so curious they'll have to scan it!
                  </p>
                </div>
              </Card>
            </div>
          )}

          {/* ── History Tab ── */}
          {activeTab === 'history' && (
            <div className="animate-fade-in">
              <Card title="Message History" action={
                messageHistory.length > 0
                  ? <span className="text-xs text-slate-400 dark:text-slate-500">{messageHistory.length} entries</span>
                  : undefined
              }>
                {loadingHistory ? (
                  <div className="text-center py-8">
                    <Loader2 size={24} className="animate-spin text-brand mx-auto mb-2" />
                    <p className="text-sm text-slate-400 dark:text-slate-500">Loading history...</p>
                  </div>
                ) : messageHistory.length > 0 ? (
                  <div className="space-y-3">
                    {messageHistory.slice(0, 10).map((entry, index) => (
                      <div key={index} className="bg-slate-50 dark:bg-slate-800/50 rounded-xl p-4 border-l-4 border-brand/40">
                        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">
                          <div className="flex gap-3 flex-1 min-w-0">
                            {entry.imageUrl && (
                              <img
                                src={entry.imageUrl}
                                alt="Historical"
                                className="w-16 h-16 min-w-[4rem] object-cover rounded-lg border border-slate-200 dark:border-slate-700"
                                onError={(e: React.SyntheticEvent<HTMLImageElement>) => { e.currentTarget.style.display = 'none' }}
                              />
                            )}
                            <p className="text-sm text-slate-700 dark:text-slate-300 whitespace-pre-wrap break-words">{entry.redirectUrl ? `Redirect: ${entry.redirectUrl}` : entry.message}</p>
                          </div>
                          <div className="flex flex-col sm:items-end gap-1 shrink-0">
                            <span className={`inline-block px-2 py-0.5 text-xs rounded-full w-fit font-medium ${
                              entry.action === 'created'
                                ? 'bg-accent-success/10 text-accent-success'
                                : 'bg-brand/10 text-brand dark:text-blue-400'
                            }`}>
                              {entry.action === 'created' ? 'Created' : 'Updated'}
                            </span>
                            <p className="text-xs text-slate-400 dark:text-slate-500">
                              {timeAgo(entry.timestamp)}
                            </p>
                            {entry.action !== 'created' && (
                              <button
                                onClick={() => {
                                  setNewMessage(entry.message)
                                  setNewImageUrl(entry.imageUrl || '')
                                  setRedirectUrl(entry.redirectUrl || '')
                                  setRedirectDelaySeconds(entry.redirectDelaySeconds ?? 0)
                                  setActiveTab('message')
                                }}
                                className="text-xs text-brand hover:text-brand-600 dark:text-blue-400 hover:underline font-medium"
                              >
                                Restore
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-slate-400 dark:text-slate-500 text-center py-8">No history yet — your message changes will appear here.</p>
                )}
              </Card>
            </div>
          )}

          {/* ── QR Code Tab ── */}
          {activeTab === 'analytics' && (
            <div className="space-y-5 animate-fade-in">
              {!analytics ? <Card><Loader2 size={24} className="animate-spin text-brand mx-auto" /></Card> : <>
                <div className="grid grid-cols-3 gap-3">
                  {[['Total', analytics.totalScans], ['Unique', analytics.uniqueScans], ['Bots filtered', analytics.filteredBots]].map(([label, value]) => (
                    <Card key={String(label)} className="!p-4 text-center"><p className="text-xs text-slate-500">{label}</p><p className="text-2xl font-bold">{value}</p></Card>
                  ))}
                </div>
                <Card title="Last 30 days">
                  <div className="flex items-end gap-1 h-40">
                    {analytics.daily.map((point) => {
                      const max = Math.max(...analytics.daily.map((item) => item.scans), 1)
                      return <div key={point.date} className="flex-1 bg-brand/80 rounded-t" style={{ height: `${Math.max(4, (point.scans / max) * 100)}%` }} title={`${point.date}: ${point.scans}`} />
                    })}
                  </div>
                </Card>
                <div className="grid sm:grid-cols-2 gap-5">
                  <Card title="Top referrers">{Object.entries(analytics.referrers).sort((a,b) => b[1]-a[1]).slice(0,8).map(([name,count]) => <div key={name} className="flex justify-between py-1 text-sm"><span>{name}</span><strong>{count}</strong></div>)}</Card>
                  <Card title="Approximate regions">{Object.entries(analytics.regions).sort((a,b) => b[1]-a[1]).slice(0,8).map(([name,count]) => <div key={name} className="flex justify-between py-1 text-sm"><span>{name}</span><strong>{count}</strong></div>)}</Card>
                </div>
                <Card><p className="text-sm text-slate-500 mb-3">Scan-event detail is retained for {analytics.retentionDays} days.</p><div className="flex flex-wrap gap-2"><Button variant="secondary" onClick={async () => { const token = await getAccessToken(); const response = await fetch(buildApiUrl(`/messages/${handle}/analytics?format=csv`), { headers: { Authorization: `Bearer ${token}` } }); const blob = await response.blob(); const link = document.createElement('a'); link.href = URL.createObjectURL(blob); link.download = `${handle}-scans.csv`; link.click(); URL.revokeObjectURL(link.href) }}>Export CSV</Button><Button variant="danger" onClick={() => void clearAnalytics()}>Delete scan details</Button></div></Card>
              </>}
            </div>
          )}

          {/* ── QR Code Tab ── */}
          {activeTab === 'qr' && (
            <div className="space-y-5 animate-fade-in">
              <Card title="Your QR Code">
                <div className="flex flex-col items-center gap-6 py-4">
                  <div className="p-4 bg-white rounded-2xl shadow-lift border border-slate-100">
                    <QrMaker handle={message.handle} size={240} darkColor={appearance.qrDarkColor} lightColor={appearance.qrLightColor} frame={appearance.qrFrame} />
                  </div>
                  {message.viewCount != null && message.viewCount > 0 && (
                    <p className="text-sm text-slate-500 dark:text-slate-400 font-medium">
                      Scanned <span className="text-slate-800 dark:text-slate-200 font-semibold">{message.viewCount.toLocaleString()}</span> {message.viewCount === 1 ? 'time' : 'times'}
                    </p>
                  )}
                  <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto sm:min-w-[240px]">
                    <Button variant="primary" size="lg" onClick={handleDownloadQr} className="flex-1">
                      <Download size={16} className="mr-2" />
                      Download PNG
                    </Button>
                    <Button variant="secondary" size="lg" onClick={handleDownloadSvg} className="flex-1">
                      <Download size={16} className="mr-2" />
                      Download SVG
                    </Button>
                  </div>
                  <div className="text-center">
                    <p className="text-xs text-slate-400 dark:text-slate-500 mb-1">Your QR code URL</p>
                    <a
                      href={buildQrUrl(message.handle)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-brand hover:text-brand-600 underline break-all dark:text-blue-400"
                    >
                      {buildQrUrl(message.handle).replace('https://', '')}
                    </a>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 w-full">
                    <label className="text-xs font-medium text-slate-500">QR foreground<input type="color" value={appearance.qrDarkColor} onChange={(e) => setAppearance((current) => ({ ...current, qrDarkColor: e.target.value }))} className="block w-full h-10 mt-1" /></label>
                    <label className="text-xs font-medium text-slate-500">QR background<input type="color" value={appearance.qrLightColor} onChange={(e) => setAppearance((current) => ({ ...current, qrLightColor: e.target.value }))} className="block w-full h-10 mt-1" /></label>
                    <label className="text-xs font-medium text-slate-500">Frame<select value={appearance.qrFrame} onChange={(e) => setAppearance((current) => ({ ...current, qrFrame: e.target.value as CodeAppearance['qrFrame'] }))} className="block w-full h-10 mt-1 rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800"><option value="none">None</option><option value="rounded">Rounded</option><option value="label">YOWO label</option></select></label>
                  </div>
                  <Button variant="secondary" onClick={(event) => handleUpdateMessage(event as unknown as React.FormEvent)}>Save QR Style</Button>
                </div>
              </Card>

              <Card title="Share">
                <div className="flex flex-col sm:flex-row gap-3">
                  <Button variant="primary" className="flex-1" onClick={() => window.open(buildQrUrl(message.handle), '_blank')}>
                    <Link2 size={15} className="mr-2" />
                    View Public Page
                  </Button>
                  <Button variant="secondary" className="flex-1" onClick={() => copyToClipboard(buildQrUrl(message.handle))}>
                    {copied
                      ? <><Check size={15} className="mr-2 text-accent-success" />Copied!</>
                      : <><Copy size={15} className="mr-2" />Copy URL</>
                    }
                  </Button>
                  <Button variant="secondary" className="flex-1" onClick={() => void shareCode(message.handle)}>
                    <Share2 size={15} className="mr-2" />Share
                  </Button>
                </div>
              </Card>

              <Card title="Print & Wear Your Code">
                <div className="space-y-4">
                  <p className="text-sm text-slate-500 dark:text-slate-400 leading-relaxed">
                    Slap your code on stickers, shirts, anything — every scan goes straight to your message. Your QR has the YOWO logo baked in so people know where it's from.
                  </p>
                  <Button variant="primary" size="lg" className="w-full" onClick={handleGetStickers}>
                    <Printer size={16} className="mr-2" />
                    Get Stickers — downloads your QR &amp; opens Sticker Mule
                  </Button>
                  <Button variant="secondary" size="lg" className="w-full" onClick={handleDownloadPrintSheet}>
                    <Download size={16} className="mr-2" />
                    Download Letter-Size Sticker Sheet
                  </Button>
                  <p className="text-xs text-slate-400 dark:text-slate-500">
                    Or upload your downloaded PNG to any print service:
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <a href="https://www.printful.com/custom/stickers" target="_blank" rel="noopener noreferrer"
                      className="inline-flex items-center px-3 py-1.5 rounded-lg bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-xs font-medium text-slate-700 dark:text-slate-300 transition-colors">
                      Printful
                    </a>
                    <a href="https://www.vistaprint.com/stickers" target="_blank" rel="noopener noreferrer"
                      className="inline-flex items-center px-3 py-1.5 rounded-lg bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-xs font-medium text-slate-700 dark:text-slate-300 transition-colors">
                      Vistaprint
                    </a>
                    <a href="https://www.redbubble.com" target="_blank" rel="noopener noreferrer"
                      className="inline-flex items-center px-3 py-1.5 rounded-lg bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-xs font-medium text-slate-700 dark:text-slate-300 transition-colors">
                      Redbubble
                    </a>
                    <a href="https://www.canva.com" target="_blank" rel="noopener noreferrer"
                      className="inline-flex items-center px-3 py-1.5 rounded-lg bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-xs font-medium text-slate-700 dark:text-slate-300 transition-colors">
                      Canva
                    </a>
                  </div>
                </div>
              </Card>
            </div>
          )}

          {/* ── Settings Tab ── */}
          {activeTab === 'settings' && (
            <div className="space-y-5 animate-fade-in">
              <Card title="Navigation">
                <div className="grid grid-cols-2 gap-3">
                  <Button variant="ghost" className="justify-start" onClick={() => navigate('/')}>
                    <Home size={15} className="mr-2" />
                    Home
                  </Button>
                  <Button variant="ghost" className="justify-start" onClick={() => navigate('/setup')}>
                    <Plus size={15} className="mr-2" />
                    Create New
                  </Button>
                  <Button variant="ghost" className="justify-start" onClick={() => navigate('/my-codes')}>
                    <MessageSquare size={15} className="mr-2" />
                    My Codes
                  </Button>
                </div>
              </Card>

              <Card>
                <Button variant="danger" className="w-full" onClick={handleLogout}>
                  <LogOut size={15} className="mr-2" />
                  Log Out
                </Button>
              </Card>

              <Card>
                <div className="space-y-3">
                  <div>
                    <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">Delete Code</p>
                    <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">Permanently removes this code, its message, and all history. This cannot be undone.</p>
                  </div>
                  {deleteError && <Alert variant="danger">{deleteError}</Alert>}
                  {!confirmDelete ? (
                    <Button variant="danger" className="w-full" onClick={() => setConfirmDelete(true)}>
                      <Trash2 size={15} className="mr-2" />
                      Delete This Code
                    </Button>
                  ) : (
                    <div className="space-y-2">
                      <p className="text-sm font-medium text-red-600 dark:text-red-400">
                        Delete <span className="font-mono">/{handle}</span>? This cannot be undone.
                      </p>
                      <div className="flex gap-2">
                        <Button variant="secondary" className="flex-1" onClick={() => setConfirmDelete(false)} disabled={deleting}>
                          Cancel
                        </Button>
                        <Button variant="danger" className="flex-1" onClick={handleDeleteCode} disabled={deleting}>
                          {deleting ? 'Deleting...' : 'Yes, Delete'}
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              </Card>
            </div>
          )}
        </div>
      </div>
    </>
  )
}

export default Dashboard
